import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Building2, Plus } from "lucide-react";
import { PageHeader, AppButton, SearchInput, ConfirmModal, Paginator } from "../../ui";
import { FilterBar, FilterSelect } from "../../ui/FilterBar";
import InstituteRow from "./InstituteRow";
import InstituteFormModal from "./InstituteFormModal";
import apiClient from "../../../utils/apiClient";
import "./institutes.scss";

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "inactive", label: "Inactive" },
  { value: "suspended", label: "Suspended" },
];

const PAGE_SIZE = 25;

const EMPTY_FORM = { name: "", email: "", phone: "", address: "", currency: "INR", status: "active", gstNumber: "", subdomain: "" };

const InstitutesListPage = () => {
  const [institutes, setInstitutes] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [plans, setPlans] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Debounce search
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [search]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, planFilter]);

  // Plan dropdown options (fetched once)
  const planOptions = useMemo(() => ([
    { value: "all", label: "All Plans" },
    { value: "__none__", label: "No Plan" },
    ...plans.map((p) => ({ value: p._id, label: p.name })),
  ]), [plans]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get("/sa/plans");
        setPlans(res.data.plans || res.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const fetchInstitutes = useCallback(async () => {
    try {
      const params = { page, limit: PAGE_SIZE };
      if (statusFilter !== "all") params.status = statusFilter;
      if (planFilter !== "all") params.plan = planFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await apiClient.get("/sa/institutes", { params });
      setInstitutes(res.data.institutes || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch {
      setInstitutes([]);
      setTotal(0);
      setPages(1);
    }
  }, [page, statusFilter, planFilter, debouncedSearch]);

  useEffect(() => { fetchInstitutes(); }, [fetchInstitutes]);

  const handleCreate = async (data) => {
    try {
      setFormLoading(true);
      await apiClient.post("/sa/institutes", data);
      await fetchInstitutes();
      setShowForm(false);
      setFormData(EMPTY_FORM);
    } catch {
      // handle error
    } finally {
      setFormLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await apiClient.patch(`/sa/institutes/${id}/status`, { status: newStatus });
      setInstitutes((prev) => prev.map((i) => (i._id === id ? { ...i, status: newStatus } : i)));
    } catch {
      // handle error
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await apiClient.delete(`/sa/institutes/${deleteId}`);
      await fetchInstitutes();
      setDeleteId(null);
    } catch {
      // handle error
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="sa-institutes">
      <PageHeader
        title="Institutes"
        description={`${total} registered institutes`}
        icon={Building2}
        actions={
          <AppButton variant="default" size="sm" onClick={() => setShowForm(true)}>
            <Plus size={16} /> Add Institute
          </AppButton>
        }
      />

      {/* Filters */}
      <FilterBar
        actions={
          <SearchInput value={search} onChange={setSearch} placeholder="Search institutes…" />
        }
      >
        <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
        <FilterSelect label="Plan" value={planFilter} onChange={setPlanFilter} options={planOptions} />
      </FilterBar>

      {/* Table */}
      <div className="sa-table-card">
        <div className="sa-table-scroll">
          <table className="sa-mini-table">
            <thead>
              <tr>
                <th>Institute</th>
                <th>Subdomain</th>
                <th>Plan</th>
                <th>Students</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {institutes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-5 text-muted" style={{ fontSize: "0.8125rem" }}>
                    No institutes match your filters
                  </td>
                </tr>
              ) : (
                institutes.map((inst) => (
                  <InstituteRow
                    key={inst._id}
                    institute={inst}
                    onStatusChange={handleStatusChange}
                    onDelete={setDeleteId}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Paginator
        page={page}
        totalPages={pages}
        total={total}
        pageSize={PAGE_SIZE}
        onChange={setPage}
        infoLabel="institutes"
      />

      {/* Create Modal */}
      <InstituteFormModal
        show={showForm}
        onHide={() => { setShowForm(false); setFormData(EMPTY_FORM); }}
        onSubmit={handleCreate}
        formData={formData}
        setFormData={setFormData}
        loading={formLoading}
      />

      {/* Delete Confirm */}
      <ConfirmModal
        show={!!deleteId}
        onHide={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Institute"
        message="This will permanently delete this institute and all its data. This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default InstitutesListPage;
